import { css } from '@styled-system/css';
import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  LinearScale,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import type { Plugin } from 'chart.js';
import { type FC } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@/app/providers/ThemeProvider';
import { Message } from '@/shared/components/ui/Message';
import { Spinner } from '@/shared/components/ui/Spinner';
import { dsTokens } from '@/shared/theme/tokens';
import { formatHoursShort, type SaleTimeBucket } from '@/shared/utils/analysis/saleTime';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

// ============================================================================
// Répartition des temps de vente en barres (buckets d'heures), même style que
// la répartition des prix, avec un marqueur vertical « Repost » : le délai
// sous lequel 80 % des ventes sont faites (au-delà, mieux vaut reposter).
// ============================================================================

export type { SaleTimeBucket } from '@/shared/utils/analysis/saleTime';

interface SaleTimeDistributionBarsProps {
  buckets: SaleTimeBucket[] | null;
  /** Seuil de repost (P80) en heures — marqueur vertical. */
  repostHours: number | null;
  loading?: boolean;
}

const containerStyles = css({
  width: '100%',
  position: 'relative',
});

const centered = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '280px',
});

/** Pixel X d'une durée sur l'axe catégoriel des buckets (interpolation). */
const pixelForHours = (chart: ChartJS, buckets: SaleTimeBucket[], hours: number): number | null => {
  const scale = chart.scales.x;
  if (!scale) return null;

  let lower = 0;
  for (let i = 0; i < buckets.length; i += 1) {
    const upper = buckets[i].maxHours;
    if (hours <= upper || i === buckets.length - 1) {
      const ratio = upper === lower ? 0.5 : Math.min(Math.max((hours - lower) / (upper - lower), 0), 1);
      const center = scale.getPixelForValue(i);
      const halfWidth =
        buckets.length > 1
          ? Math.abs(
              scale.getPixelForValue(Math.min(i + 1, buckets.length - 1)) - scale.getPixelForValue(Math.max(i - 1, 0)),
            ) / (i === 0 || i === buckets.length - 1 ? 2 : 4)
          : 40;
      return center - halfWidth + ratio * halfWidth * 2;
    }
    lower = upper;
  }
  return null;
};

export const SaleTimeDistributionBars: FC<SaleTimeDistributionBarsProps> = ({ buckets, repostHours, loading }) => {
  const { t } = useTranslation();
  const { theme: colorMode } = useTheme();
  const ds = dsTokens[colorMode];

  if (loading) {
    return (
      <div className={centered}>
        <Spinner size={24} />
      </div>
    );
  }

  if (!buckets || buckets.length === 0) {
    return (
      <Message type="info" bordered>
        {t('statsPanel.noDistribution')}
      </Message>
    );
  }

  const barColor = ds['brand.primary'];
  const repostColor = ds['success.default'];
  const labelColor = ds['text.secondary'];

  // Marqueur vertical « Repost » (P80) dessiné par un plugin inline.
  const markersPlugin: Plugin<'bar'> = {
    id: 'repostMarker',
    afterDatasetsDraw: (chart) => {
      const { ctx, chartArea } = chart;
      if (!chartArea || repostHours == null) return;

      const x = pixelForHours(chart, buckets, repostHours);
      if (x == null || x < chartArea.left || x > chartArea.right) return;

      ctx.save();
      ctx.strokeStyle = repostColor;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top + 14);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = repostColor;
      ctx.font = '600 11px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(t('statsPanel.markerRepost'), x, chartArea.top + 8);
      ctx.restore();
    },
  };

  const chartData: ChartData<'bar'> = {
    labels: buckets.map((bucket) => formatHoursShort(bucket.maxHours)),
    datasets: [
      {
        data: buckets.map((bucket) => bucket.count),
        backgroundColor: `${barColor}B3`, // ~70% alpha
        hoverBackgroundColor: barColor,
        borderRadius: 4,
        borderSkipped: false,
        barPercentage: 0.85,
        categoryPercentage: 0.9,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: ds['surface.elevated'],
        titleColor: ds['text.primary'],
        bodyColor: ds['text.secondary'],
        borderColor: ds['border.default'],
        borderWidth: 1,
        padding: 8,
        displayColors: false,
        callbacks: {
          title: (items: TooltipItem<'bar'>[]) => {
            const index = items[0].dataIndex;
            const maxHours = buckets[index].maxHours;
            const minHours = index === 0 ? 0 : buckets[index - 1].maxHours;
            return `${formatHoursShort(minHours)} – ${formatHoursShort(maxHours)}`;
          },
          label: (item: TooltipItem<'bar'>) => t('statsPanel.tooltipSold', { count: buckets[item.dataIndex].count }),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: ds['text.tertiary'], font: { size: 11 } },
        border: { color: ds['border.default'] },
      },
      y: {
        beginAtZero: true,
        grid: { color: `${ds['border.subtle']}80` },
        ticks: { color: ds['text.tertiary'], font: { size: 11 }, precision: 0 },
        border: { display: false },
      },
    },
  };

  return (
    <div className={containerStyles} style={{ height: 280, color: labelColor }}>
      <Bar data={chartData} options={options} plugins={[markersPlugin]} />
    </div>
  );
};
